import { getPreferredPhone, getProfilePhoto, useDeleteContact, useGetAllContacts, useGetContact, useUpdateContact } from "@api/contactService";
import React, { memo, useContext, useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ObjectShow from "@components/objects/ObjectShow";
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import ObjectColumn from "@components/objects/ObjectColumn";
import { ObjectPrimaryInfo } from "@components/objects/ObjectPrimaryInfo";
import Avatar from "@mui/joy/Avatar";
import { ObjectContext } from "@providers/ObjectProvider";
import { ensureDateObject, formatAbsoluteDate, formatDate, isValidDate } from "@utils/dates";
import ObjectPreview from "@components/objects/ObjectPreview";
import { generateAddress } from "@utils/generateAddress";
import { CardOverflow, List, ListDivider, ListItem, ListItemContent, ListItemDecorator, Table } from "@mui/joy";
import AbstractObjectRelationship from "@components/objects/AbstractObjectRelationship";
import UserAccessCardEdit from "@components/objects/UserAccessCardEdit";
import { intervalToDuration } from "date-fns";
import ObjectEdit from "@components/objects/ObjectEdit";
import { ObjectPrimaryInfoEdit } from "@components/objects/ObjectPrimaryInfoEdit";
import { ObjectRelationshipEdit } from "@components/objects/ObjectRelationshipEdit";
import { countries } from "@utils/selectOptions";
import { ObjectRelationship } from "@components/objects/ObjectRelationship";
import SearchSelect from "@components/SearchSelect";
import { CheckPermission } from "@components/CheckPermission";
import { ContactInfoShow } from "@components/objects/ContactInfoShow";



// import { DynamicFieldData } from "./dynamic-control-types";

const fields /*: DynamicFieldData[]*/ = [
    {
        name: 'profile_image',
        type: 'file',
        label: 'Photo',
        scope: 'profile',
        url: (object) => getProfilePhoto(object)?.url,
    },

    {name: 'title', type: 'text', placeholder: 'e.g.CEO'},
    {
        name: 'first_name',
        type: 'text',
        placeholder: 'e.g. Marsha',
        showRequired: (object) => !object.company_name && !object.first_name,
        config: {
            validate: (value, object) => {
                if (!value && !object.company_name) return 'Required if Company is blank';
                return true;
            },
        },
    },
    {name: 'last_name', type: 'text', placeholder: 'e.g. MainStreet'},
    {
        name: 'company_name',
        label: 'Company',
        type: 'text',
        placeholder: 'e.g. Marsha\'s Truffles',
        showRequired: (object) => !object.company_name && !object.first_name,
        config: {
            validate: (value, object) => {
                if (!value && !object.first_name) return 'Required if First Name is blank';
                return true;
            },
        },
    },
    {
        name: 'do_not_contact',
        label: 'Communications',
        type: 'date_boolean',
        inputLabel: 'Do Not Contact',
    },
    {name: 'email', type: 'text', placeholder: 'e.g. marsha@tastytruffles.net'},
    {name: 'alternate_email', label: <>Alternate<br/>Email(s)</>, type: 'text', placeholder: 'e.g. marsha@tastytruffles.net'},
    {name: 'birthday', type: 'date', placeholder: 'e.g. 05/23/2016'},
    {name: 'deceased', type: 'date', placeholder: 'e.g. 05/23/2016'},
    {
        name: 'spouse_id',
        type: 'relationship',
        endpoint: 'contacts',
        defaultProp: 'spouse',
    },
    {name: 'anniversary', type: 'date', placeholder: 'e.g. 05/23/2016'},
    {name: 'address', type: 'text', placeholder: 'e.g. 123 Main Street'},
    {name: 'address_2', label: <>Address <br/>(cont.)</>, type: 'text', placeholder: 'e.g. Suite A'},
    {name: 'country', type: 'select', options: countries, defaultValue: ({tenant}) => tenant.country},
    {
        name: 'zip_postal',
        label:<>Zip/<wbr/>Postal</>,
        type: 'text',
        placeholder: 'e.g. 61325',
        defaultValue: ({tenant}) => tenant.postal,
        /*
        onChange: (value, formState_wip) => {
            prefillCityProvince
        }
        */
    },
    {name: 'city', type: 'text', placeholder: 'e.g. MainStreet', defaultValue: ({tenant}) => tenant.city,},
    {name: 'state_province', label: 'State', type: 'text', placeholder: 'e.g. IL', defaultValue: ({tenant}) => tenant.state,},
    {name: 'work_phone', label: 'Phone (Work)', type: 'text', placeholder: 'e.g. (000) 000-0000'},
    {name: 'cell_phone', label: 'Phone (Cell)', type: 'text', placeholder: 'e.g. (000) 000-0000'},
    {name: 'home_phone', label: 'Phone (Home)', type: 'text', placeholder: 'e.g. (000) 000-0000'},
    {
        name: 'preferred_contact_method',
        label: <>Preferred<br/>Contact Method</>,
        type: 'select',
        // placeholder: 'Choose...',
        options: [
            {label: 'Home Phone', value: 'home'},
            {label: 'Work Phone', value: 'work'},
            {label: 'Cell Phone', value: 'cell'},
            {label: 'Email', value: 'email'},
        ],
    },
    // {name: 'url', label: 'Website', type: 'text', placeholder: 'e.g. https://tastytruffles.net'},
    {name: 'facebook_url', label: 'Facebook', type: 'text', placeholder: 'e.g. https://www.facebook.com/marshastastytruffles'},
    {name: 'instagram_url', label: 'Instagram', type: 'text', placeholder: 'e.g. https://www.instagram.com/marshastastytruffles'},
    // {name: 'twitter_url', label: 'X (Twitter)', type: 'text', placeholder: 'e.g. https://twitter.com/marshastastytruffles'},
    {name: 'linkedin_url', label: 'LinkedIn', type: 'text', placeholder: 'e.g. https://www.linkedin.com/marshastastytruffles'},
    // {name: 'youtube_url', label: 'YouTube', type: 'text', placeholder: 'e.g. https://www.youtube.com/@marshastastytruffles'},
    // {name: 'yelp_url', label: 'Yelp', type: 'text', placeholder: 'e.g. https://www.yelp.com/biz/tastytruffles'},

    {
        name: 'demographics',
        label: 'Demographics',
        type: 'demographics',
        render: () => {

        },
    },
];

const ContactEdit = memo(function ContactEdit(props) {
    return (
        <ObjectEdit
            query={useGetContact}
            mutation={useUpdateContact}
            deleteMutation={useDeleteContact}
            type={'contact'}
            permissionType={'contacts'}
            title={'Edit Contact'}
            subtitleKey={'name'}
            color={'contact'}
            {...props}
        >
            <ObjectColumn primary>
                <CheckPermission permissions={['edit contacts', 'edit basic contacts']}>
                    <ObjectPrimaryInfoEdit
                        title={'Contact Info'}
                        tint={'contact'}
                        fields={fields}
                    />
                </CheckPermission>

                <CheckPermission permissions={['edit contacts', 'edit basic contacts']} fails>
                    <ContactInfoShow/>
                </CheckPermission>

                <CheckPermission permissions={'manage users'}>
                    <UserAccessCardEdit />
                </CheckPermission>

                <CheckPermission permissions={'edit basic contacts'}>
                    {/* note: we leave this as the edit object because it handles degredation gracefully */}
                    <ObjectRelationshipEdit type={'properties'} permissions={'edit contacts'} />
                    <ObjectRelationshipEdit type={'owned_properties'} permissions={'edit contacts'} />
                    <ObjectRelationshipEdit type={'businesses'} permissions={'edit contacts'} />
                </CheckPermission>
            </ObjectColumn>

            <ObjectColumn>
                <ObjectRelationshipEdit type={'properties'} permissions={'edit contacts'} />
                <ObjectRelationshipEdit type={'owned_properties'} permissions={'edit contacts'} />
                <ObjectRelationshipEdit type={'businesses'} permissions={'edit contacts'} />
                <ObjectRelationship type={'workplan_tasks'} />
                <ObjectRelationshipEdit type={'workplans'} permissions={'edit contacts'} />
                <ObjectRelationshipEdit type={'volunteer_hours'} permissions={'edit contacts'} />
                <ObjectRelationship
                    type={'donation_entries'}
                    permissions={'view donations'}
                />
            </ObjectColumn>

            <ObjectColumn>
                <ObjectRelationshipEdit type={'notes'} permissions={'edit contacts'} />
                <ObjectRelationshipEdit type={'contact_reports'} />
                <ObjectRelationshipEdit type={'involvements'} permissions={'edit contacts'} />
                <ObjectRelationshipEdit type={'files'} permissions={'edit contacts'} />
            </ObjectColumn>

        </ObjectEdit>
    )
});
export default ContactEdit;
